import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import apiClient from "../../apiClient";
import "./PeoplePlan.css";
import MUMTable from "./MUMTable";
import NewHiresTable from "./NewHiresTable";
import RestaurantManagersTable from "./Tables/RestaurantManagersTable";
import AssistantManagersTable from "./Tables/AssistantManagersTable";
import ShiftLeadersTable from "./Tables/ShiftLeadersTable";
import CrewMemberTable from "./Tables/CrewMemberTable";
import SpreadsheetView from "../SpreadsheetView/SpreadsheetView";
import ViewToggle from "../SpreadsheetView/ViewToggle";
import TheBench from "./TheBench/TheBench";

const PeoplePlan = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mumFromQuery = queryParams.get("mum");
  const viewFromQuery = queryParams.get("view");

  const [selectedMUM, setSelectedMUM] = useState(mumFromQuery || "");
  const [mumList, setMumList] = useState([]);
  const [storeData, setStoreData] = useState([]);
  const [enrichedStoreData, setEnrichedStoreData] = useState([]);
  const [currentView, setCurrentView] = useState(viewFromQuery || "all-staff");
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();

  // States to control table visibility
  const [isVisible, setIsVisible] = useState({
    AWS: true,
    NH: true,
    RM: true,
    AM: true,
    SL: true,
    CM: true,
  });

  // Check for authentication and get user role
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("authToken");
          navigate("/");
        } else {
          // Get role from multiple possible sources
          const role =
            decodedToken.role?.toLowerCase() ||
            localStorage.getItem("userRole")?.toLowerCase() ||
            null;

          console.log("PeoplePlan - User Role:", role);
          setUserRole(role);
        }
      } catch (error) {
        console.error("Invalid token:", error);
        localStorage.removeItem("authToken");
        navigate("/");
      }
    }
  }, [navigate]);

  // Fetch the list of unique MUMs
  useEffect(() => {
    const fetchMUMs = async () => {
      try {
        const response = await apiClient.get(`/api/mums?ts=${Date.now()}`);
        const data = response.data;
        const sortedMums = data.map((mum) => mum.mum).sort();
        setMumList(sortedMums);
      } catch (error) {
        console.error("Error fetching MUMs:", error);
      }
    };
    fetchMUMs();
  }, []);

  // Fetch store data based on the selected MUM (only for all-staff view)
  useEffect(() => {
    if (selectedMUM && currentView === "all-staff") {
      const fetchStores = async () => {
        try {
          const response = await apiClient.get(
            `/api/current-staffing?mum=${encodeURIComponent(
              selectedMUM
            )}&ts=${Date.now()}`
          );
          setStoreData(response.data);
        } catch (error) {
          console.error("Error fetching store data:", error);
        }
      };
      fetchStores();
    }
  }, [selectedMUM, currentView]);

  // Handle MUM dropdown change and update the URL
  const handleMUMChange = (value) => {
    setSelectedMUM(value);
    navigate(
      `/peopleplan?mum=${encodeURIComponent(value)}&view=${currentView}`,
      { replace: true }
    );
  };

  // Handle view change
  const handleViewChange = (view) => {
    setCurrentView(view);
    const params = new URLSearchParams(location.search);
    params.set("view", view);
    if (selectedMUM) {
      params.set("mum", selectedMUM);
    }
    navigate(`/peopleplan?${params.toString()}`, { replace: true });
  };

  // Navigate to employee profile with the selected MUM in the query parameters
  const handleNavigateToProfile = (userId) => {
    navigate(`/employee/${userId}?mum=${encodeURIComponent(selectedMUM)}`);
  };

  // Handle checkbox change to toggle table visibility
  const handleCheckboxChange = (tableKey) => {
    setIsVisible((prev) => ({
      ...prev,
      [tableKey]: !prev[tableKey],
    }));
  };

  // ✅ MODIFIED: Check if user can select MUM
  const canSelectMUM = () => {
    const userEmail = localStorage.getItem("userEmail")?.toLowerCase();
    
    // Super admins can always select
    const superAdminEmails = [
      "lbarbosa@cafuamanagement.com",
      "lmarques@cafuamanagement.com", // ✅ ADD LUIS
    ];
    
    if (superAdminEmails.includes(userEmail)) {
      console.log("Super admin - MUM selection enabled");
      return true;
    }
    
    // Role-based check
    const allowedRoles = ["admin", "vp", "director", "mum"];
    const canSelect = allowedRoles.includes(userRole);
    
    console.log("canSelectMUM check:", {
      userRole,
      userEmail,
      canSelect
    });
    
    return canSelect;
  };

  // Callback to receive enriched store data from MUMTable
  const handleEnrichedDataFetch = useCallback((enrichedData) => {
    setEnrichedStoreData(enrichedData);
  }, []);

  // Get the appropriate store data (enriched if available, otherwise basic)
  const getStoreDataForTables = () => {
    return enrichedStoreData.length > 0 ? enrichedStoreData : storeData;
  };

  return (
    <div className="people-plan-container">
      {/* Control bar with MUM/checkboxes on left, view toggle on right */}
      <div className="control-bar">
        <div className="control-bar-left">
          {/* MUM Selector - Hide for Bench views */}
          {currentView !== "bench-table" &&
            currentView !== "bench-kanban" &&
            currentView !== "pic-tracker" && (
              <div className="mum-selector-group">
                <label htmlFor="mum-selector">Select MUM: </label>
                <select
                  id="mum-selector"
                  value={selectedMUM}
                  onChange={(e) => handleMUMChange(e.target.value)}
                  disabled={!canSelectMUM()}
                  title={
                    !canSelectMUM()
                      ? "MUM selection is restricted to your assigned MUM"
                      : ""
                  }
                >
                  <option value="">MUM</option>
                  {mumList.map((mum, index) => (
                    <option key={index} value={mum}>
                      {mum}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {/* Visibility checkboxes - only show for all-staff view */}
          {currentView === "all-staff" && (
            <div className="table-visibility-controls">
              <label>
                <input
                  type="checkbox"
                  checked={isVisible.AWS}
                  onChange={() => handleCheckboxChange("AWS")}
                />
                AWS
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={isVisible.NH}
                  onChange={() => handleCheckboxChange("NH")}
                />
                NH
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={isVisible.RM}
                  onChange={() => handleCheckboxChange("RM")}
                />
                RM
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={isVisible.AM}
                  onChange={() => handleCheckboxChange("AM")}
                />
                AM
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={isVisible.SL}
                  onChange={() => handleCheckboxChange("SL")}
                />
                SL
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={isVisible.CM}
                  onChange={() => handleCheckboxChange("CM")}
                />
                CM
              </label>
            </div>
          )}
        </div>

        {/* View Toggle on the right */}
        <div className="control-bar-right">
          <ViewToggle
            currentView={currentView}
            onViewChange={handleViewChange}
          />
        </div>
      </div>

      {/* Render views based on currentView */}
      {currentView === "pic-tracker" ? (
        <SpreadsheetView
          selectedMUM={selectedMUM}
          onMUMChange={handleMUMChange}
          mumList={mumList}
        />
      ) : currentView === "bench-table" || currentView === "bench-kanban" ? (
        <TheBench
          viewMode={currentView === "bench-table" ? "table" : "kanban"}
          selectedMUM={selectedMUM}
          userRole={userRole}
          isIntegrated={true}
        />
      ) : (
        /* Render All Staff View */
        <>
          {isVisible.AWS && (
            <MUMTable
              selectedMUM={selectedMUM}
              storeData={storeData}
              onDataFetch={handleEnrichedDataFetch}
            />
          )}

          {isVisible.NH && (
            <NewHiresTable
              selectedMUM={selectedMUM}
              storeData={getStoreDataForTables()}
              onNavigateToProfile={handleNavigateToProfile}
            />
          )}
          {isVisible.RM && (
            <RestaurantManagersTable
              selectedMUM={selectedMUM}
              storeData={getStoreDataForTables()}
              onNavigateToProfile={handleNavigateToProfile}
            />
          )}
          {isVisible.AM && (
            <AssistantManagersTable
              selectedMUM={selectedMUM}
              storeData={getStoreDataForTables()}
              onNavigateToProfile={handleNavigateToProfile}
            />
          )}
          {isVisible.SL && (
            <ShiftLeadersTable
              selectedMUM={selectedMUM}
              storeData={getStoreDataForTables()}
              onNavigateToProfile={handleNavigateToProfile}
            />
          )}
          {isVisible.CM && (
            <CrewMemberTable
              selectedMUM={selectedMUM}
              storeData={getStoreDataForTables()}
              onNavigateToProfile={handleNavigateToProfile}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PeoplePlan;